import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    Pagination,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CreatorGridService from "../service/CreatorGridService";
import Logger from "../../services/Logger";
import { getContentLanguage, getOrderedContentLanguages, setContentLanguage } from "../../services/LangPrefs";
import GeoService from "../../services/GeoService";
import StatsByStateTable from "../../admin/pzl_common/stats/StatsByStateTable";
import PuzzleCard from "../common/PuzzleCard";
import PuzzleStateFilter from "../common/PuzzleStateFilter";
import PuzzleExcludeStateFilter from "../common/PuzzleExcludeStateFilter";
import { trackEvent } from "../../services/GoogleAnalytics";

export default function CreatorGridMain() {
    const navigate = useNavigate();
    const [contentLang, setContentLangState] = useState(getContentLanguage());
    const [isQuebec, setIsQuebec] = useState(false);
    const [puzzles, setPuzzles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);
    const [tagCounts, setTagCounts] = useState([]);
    const [tagCountsLoading, setTagCountsLoading] = useState(true);

    useEffect(() => {
        trackEvent("entered", { name: "CreatorGridMain" });
    }, []);
    const PAGE_STORAGE_KEY = "creator_grid_page";
    const STATE_FILTER_STORAGE_KEY = "creator_grid_state_filter";

    const [page, setPage] = useState(() => {
        try {
            const saved = localStorage.getItem(PAGE_STORAGE_KEY);
            return saved !== null ? Math.max(0, parseInt(saved, 10) || 0) : 0;
        } catch {
            return 0;
        }
    });
    const [pageSize] = useState(15);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const [selectedState, setSelectedState] = useState(() => {
        try {
            return localStorage.getItem(STATE_FILTER_STORAGE_KEY) || "";
        } catch {
            return "";
        }
    });
    const [selectedTags, setSelectedTags] = useState([]);
    const [excludeDiscarded, setExcludeDiscarded] = useState(true);
    const [excludeDisabled, setExcludeDisabled] = useState(true);

    useEffect(() => {
        const checkQuebec = async () => {
            try {
                const quebecStatus = await GeoService.isQuebec();
                setIsQuebec(quebecStatus);
            } catch (error) {
                Logger.error("Error checking Quebec status:", error);
                setIsQuebec(false);
            }
        };
        checkQuebec();
    }, []);

    useEffect(() => {
        const loadStats = async () => {
            setStatsLoading(true);
            try {
                const statsData = await CreatorGridService.getGridStats();
                setStats(statsData);
            } catch (error) {
                Logger.error("Error loading stats:", error);
            } finally {
                setStatsLoading(false);
            }
        };
        loadStats();
    }, [contentLang]);

    useEffect(() => {
        const loadTagCounts = async () => {
            setTagCountsLoading(true);
            try {
                const tags = await CreatorGridService.getGridTags(contentLang, "READY_TO_REVIEW,REVIEWED,PUBLISHED");
                const list = Array.isArray(tags) ? tags : [];
                const bedrockLower = "bedrock";
                const items = list
                    .map((item) => {
                        const name = typeof item === "string" ? item : (item?.tag ?? item?.name ?? String(item ?? ""));
                        const count = typeof item === "object" && item != null && typeof item.count === "number" ? item.count : 0;
                        return { tag: name, count };
                    })
                    .filter((x) => x.tag && x.count > 0 && !String(x.tag).toLowerCase().includes(bedrockLower))
                    .sort((a, b) => a.count - b.count);
                setTagCounts(items);
            } catch (error) {
                Logger.error("Error loading tag counts:", error);
                setTagCounts([]);
            } finally {
                setTagCountsLoading(false);
            }
        };
        loadTagCounts();
    }, [contentLang]);

    useEffect(() => {
        loadPuzzles();
    }, [page, contentLang, selectedState, selectedTags, excludeDiscarded, excludeDisabled]);

    const loadPuzzles = async () => {
        setLoading(true);
        try {
            const state = selectedState || null;
            const tags = selectedTags.length > 0 ? selectedTags : null;
            const result = await CreatorGridService.listGridPuzzles(page, pageSize, 'updatedAt:desc,createdAt:desc', null, state, null, contentLang, excludeDiscarded, excludeDisabled, tags);
            if (result) {
                setPuzzles(result.content || []);
                setTotalPages(result.page?.totalPages ?? 0);
                setTotalElements(result.page?.totalElements ?? 0);
            }
        } catch (error) {
            Logger.error("Error loading puzzles:", error);
            setPuzzles([]);
        } finally {
            setLoading(false);
        }
    };

    const handleContentLanguageChange = (_event, newLanguage) => {
        if (newLanguage !== null) {
            setContentLangState(newLanguage);
            setContentLanguage(newLanguage);
            setPage(0); // Reset to first page when language changes
            try {
                localStorage.setItem(PAGE_STORAGE_KEY, "0");
            } catch (_) {}
        }
    };

    const handleTagClick = (tag) => {
        setSelectedTags((prev) => (prev.includes(tag) ? [] : [tag]));
        setPage(0);
        try {
            localStorage.setItem(PAGE_STORAGE_KEY, "0");
        } catch (_) {}
    };

    const handleStateChange = (event) => {
        const value = event.target.value;
        setSelectedState(value);
        setPage(0);
        try {
            localStorage.setItem(STATE_FILTER_STORAGE_KEY, value || "");
            localStorage.setItem(PAGE_STORAGE_KEY, "0");
        } catch (_) {}
    };

    const handlePageChange = (_event, value) => {
        const newPage = value - 1; // Pagination is 1-based, but API is 0-based
        setPage(newPage);
        try {
            localStorage.setItem(PAGE_STORAGE_KEY, String(newPage));
        } catch (_) {}
    };

    const handlePuzzleClick = (puzzleId) => {
        navigate(`/creator/grid/edit/${puzzleId}`);
    };

    const handleNewPuzzle = (type) => {
        navigate(`/creator/grid/edit?type=${type}`);
    };

    const englishStats = stats?.byLanguage?.english
        ? {
            gridWordSearchByState: stats.byLanguage.english.gridWordSearchByState,
            gridHiddenMessageByState: stats.byLanguage.english.gridHiddenMessageByState
        }
        : null;
    const frenchStats = stats?.byLanguage?.french
        ? {
            gridWordSearchByState: stats.byLanguage.french.gridWordSearchByState,
            gridHiddenMessageByState: stats.byLanguage.french.gridHiddenMessageByState
        }
        : null;

    return (
        <Box sx={{ p: { xs: 1.5, sm: 4 } }}>
            {/* Top Bar - Language Buttons (Left) and Action Buttons (Right) */}
            <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                mb: 2,
                gap: { xs: 2, sm: 0 }
            }}>
                {/* Language Buttons - Left */}
                <Box>
                    <ToggleButtonGroup
                        value={contentLang}
                        exclusive
                        onChange={handleContentLanguageChange}
                        size="small"
                    >
                        {[...getOrderedContentLanguages(isQuebec)].sort((a, b) => (a.code === 'english' ? -1 : 1)).map((lang) => (
                            <ToggleButton
                                key={lang.code}
                                value={lang.code}
                                sx={{
                                    px: 2,
                                    py: 1,
                                    '&.Mui-selected': {
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: 'primary.dark',
                                        },
                                    },
                                }}
                            >
                                {lang.code === 'french' ? 'French' : 'English'}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>

                {/* Action Buttons - Right */}
                <Box sx={{ 
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', sm: 'auto auto auto' },
                    gap: 1,
                    width: { xs: '100%', sm: 'auto' }
                }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<RocketLaunchIcon />}
                        onClick={() => navigate('/creator/guide')}
                        size="small"
                        sx={{ px: 2, py: 1, whiteSpace: 'nowrap' }}
                    >
                        Quick Start
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => handleNewPuzzle('HIDDEN_MESSAGE')}
                        size="small"
                        sx={{ px: 2, py: 1, whiteSpace: 'nowrap' }}
                    >
                        New Hidden Message
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => handleNewPuzzle('WORD_SEARCH')}
                        size="small"
                        sx={{ px: 2, py: 1, whiteSpace: 'nowrap' }}
                    >
                        New Word Search
                    </Button>
                </Box>
            </Box>
            {/* Stats Section - state-based view */}
            {statsLoading && (
                <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Loading statistics...</Typography>
                </Box>
            )}
            {!statsLoading && (englishStats || frenchStats) && (
                <Box sx={{ mb: 3 }}>
                    <StatsByStateTable
                        englishStats={englishStats}
                        frenchStats={frenchStats}
                        title="Grid puzzles by state"
                        contentLanguage={contentLang}
                    />
                </Box>
            )}

            {/* Count by tags */}
            <Box sx={{ mb: 3 }}>
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
                            Count by tags
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Number of puzzles by tag that are ready to serve users (Ready to Review, Reviewed, and Published).
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 2,
                                color: "success.dark",
                                fontStyle: "italic",
                                fontWeight: "bold"
                            }}
                        >
                            Select less-count tags and create puzzles to make them BALANCE. All tags should have the same count.
                        </Typography>
                        {tagCountsLoading ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <CircularProgress size={20} />
                                <Typography variant="body2" color="text.secondary">Loading tags...</Typography>
                            </Box>
                        ) : tagCounts.length > 0 ? (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                {tagCounts.map(({ tag, count }) => {
                                    const isSelected = selectedTags.includes(tag);
                                    return (
                                        <Chip
                                            key={tag}
                                            label={`${tag} ${count}`}
                                            size="small"
                                            variant={isSelected ? "filled" : "outlined"}
                                            color={isSelected ? "primary" : "default"}
                                            onClick={() => handleTagClick(tag)}
                                            sx={{ fontSize: "0.8rem", cursor: "pointer" }}
                                        />
                                    );
                                })}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">No tags with counts</Typography>
                        )}
                    </CardContent>
                </Card>
            </Box>

            {/* State Filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <PuzzleStateFilter
                    value={selectedState}
                    onChange={handleStateChange}
                />
                <Box sx={{ flex: 1 }} />
                {!selectedState && (
                    <PuzzleExcludeStateFilter
                        excludeDiscarded={excludeDiscarded}
                        excludeDisabled={excludeDisabled}
                        onExcludeDiscardedChange={(e) => setExcludeDiscarded(e.target.checked)}
                        onExcludeDisabledChange={(e) => setExcludeDisabled(e.target.checked)}
                    />
                )}
            </Box>


            {/* Puzzles List */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : puzzles.length === 0 ? (
                <Card>
                    <CardContent>
                        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                            No grid puzzles found. Click "New Hidden Message" or "New Word Search" to create a puzzle.
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <>
                    <Grid container spacing={2}>
                        {puzzles.map((puzzle) => (
                            <Grid key={puzzle.id} size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                                <PuzzleCard
                                    puzzle={puzzle}
                                    onClick={() => handlePuzzleClick(puzzle.id)}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Pagination
                                count={totalPages}
                                page={page + 1}
                                onChange={handlePageChange}
                                color="primary"
                                size="large"
                            />
                        </Box>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                        Showing {puzzles.length} of {totalElements} puzzles
                    </Typography>
                </>
            )}
        </Box>
    );
}

